import React, { useState, useEffect } from 'react';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';

interface BarcodeScannerProps {
  onScan: (scannedValue: string) => void;
  isActive: boolean;
  className?: string;
}

/**
 * Barcode Scanner Component
 * Provides visual feedback for barcode scanning status
 */
export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  isActive,
  className = ''
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [lastScannedValue, setLastScannedValue] = useState<string>('');
  const [scanStatusMessage, setScanStatusMessage] = useState<string>('');

  const handleScan = (scannedValue: string) => {
    setIsScanning(false);
    setLastScannedValue(scannedValue);
    setScanStatusMessage(`Scanned: ${scannedValue}`);
    
    // Clear status message after 3 seconds
    setTimeout(() => {
      setScanStatusMessage('');
      setLastScannedValue('');
    }, 3000);

    onScan(scannedValue);
  };

  const { resetBuffer } = useBarcodeScanner({
    onScan: handleScan,
    isActive,
    minLength: 5, // Minimum length for inventory IDs
    maxLength: 50,
    scanTimeout: 150,
    preventDefault: true
  });

  useEffect(() => {
    if (isActive) {
      setIsScanning(true);
      // Reset scanning state after a short delay if no scan occurs
      const timeout = setTimeout(() => {
        setIsScanning(false);
      }, 1000);

      return () => clearTimeout(timeout);
    } else {
      setIsScanning(false);
      setScanStatusMessage('');
      setLastScannedValue('');
      resetBuffer();
    }
  }, [isActive, resetBuffer]);

  if (!isActive) {
    return null;
  }

  return (
    <div className={`barcode-scanner ${className}`}>
      {/* Scanner Status Indicator */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-3 h-3 rounded-full transition-colors ${
          isScanning ? 'bg-green-500 animate-pulse' : 'bg-gray-300'
        }`}></div>
        <span className="text-sm text-gray-600">
          {isScanning ? 'Scanner ready - scan a barcode' : 'Scanner active'}
        </span>
      </div>

      {/* Scanner Icon */}
      <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
        <div className="text-center">
          <svg 
            className={`w-8 h-8 mx-auto mb-2 transition-colors ${
              isScanning ? 'text-green-500' : 'text-gray-400'
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" 
            />
          </svg>
          <p className="text-sm text-gray-500">
            {isScanning ? 'Scanning...' : 'Ready to scan'}
          </p>
        </div>
      </div>

      {/* Scan Status Message */}
      {scanStatusMessage && (
        <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded-lg">
          <p className="text-sm text-green-700 text-center">{scanStatusMessage}</p>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-3 text-xs text-gray-500 text-center">
        <p>Point your barcode scanner at a product barcode</p>
        <p>The scanned value will automatically search for inventory</p>
      </div>
    </div>
  );
};
